import React, { useRef } from 'react';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, BookOpenIcon, UsersIcon, EyeIcon, BarChart3Icon, LayoutGridIcon, GraduationCapIcon, TargetIcon } from 'lucide-react';
import { useModal } from '../components/ModalContext';
export const ForSchools = () => {
  const {
    openTalkToUs
  } = useModal();
  const useCases = [{
    label: 'HQIM implementation',
    description: 'Align coaching to your curriculum',
    icon: BookOpenIcon,
    bgColor: 'bg-orange-50',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-500'
  }, {
    label: 'Expand coaching capacity',
    description: 'Do more with your existing team',
    icon: UsersIcon,
    bgColor: 'bg-purple-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-500'
  }, {
    label: 'Visibility across classrooms',
    description: 'A clear view of instructional patterns',
    icon: EyeIcon,
    bgColor: 'bg-cyan-50',
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600'
  }, {
    label: 'Real-time coaching data',
    description: "What's working while it's happening",
    icon: BarChart3Icon,
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600'
  }];
  const SectionOne = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, {
      once: true,
      margin: '-80px'
    });
    const fadeUp = (delay: number) => ({
      initial: {
        opacity: 0,
        y: 24
      },
      animate: isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      },
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
        delay
      }
    });
    return <section className="w-full py-12 md:py-20 bg-white relative" ref={ref} data-id="element-576">
        <div className="container mx-auto px-4 sm:px-6" data-id="element-577">
          <div className="max-w-2xl mx-auto" data-id="element-578">
            <motion.span {...fadeUp(0)} className="inline-block font-semibold uppercase tracking-widest text-primary-500 mb-3 text-[14px]" data-id="element-579">
              The challenge
            </motion.span>
            <motion.h2 {...fadeUp(0.1)} className="font-serif text-2xl md:text-[1.75rem] font-normal text-cyan-800 mb-5 leading-snug" data-id="element-580">
              Why coaching is hard to see at scale.
            </motion.h2>
            <div className="space-y-6" data-id="element-581">
              <motion.p {...fadeUp(0.2)} className="text-[16px] text-gray-600 leading-relaxed" data-id="element-582">
                Many leaders are left piecing together their coaching programs
                from numerous observation notes, check-ins, and reports. They
                may be able to see what happened in one classroom, but not
                whether teachers are struggling with the same things or how well
                your curriculum is landing in classrooms. <br data-id="element-583" />
                <br data-id="element-584" />
                Leaders know their coaches are doing good work. Viewing the work
                clearly across schools is the harder.
              </motion.p>
              <motion.div {...fadeUp(0.35)} className="rounded-2xl overflow-hidden shadow-xl border border-gray-200/60" data-id="element-585">
                <img src="https://cdn.magicpatterns.com/uploads/bPL9N8jmCLpYYSGegkdtpC/pasted-image.png" alt="Nisa Coaching Hub dashboard showing teacher cards, AI chat, and coaching summaries" className="w-full h-auto" data-id="element-586" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>;
  };
  return <div className="flex flex-col min-h-screen" data-id="element-587">
      <main className="relative flex-grow overflow-hidden" data-id="element-588">
        {/* Background blobs */}
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] -z-10" data-id="element-589" />
        <div className="absolute -top-[200px] left-1/3 w-[1200px] h-[1200px] bg-gradient-to-br from-cyan-400/20 via-cyan-300/15 to-primary-200/10 rounded-[45%_55%_50%_50%] blur-3xl -z-10" data-id="element-590" />
        {/* Extended blob into content area */}
        <div className="absolute top-[60%] -right-[300px] w-[900px] h-[900px] bg-gradient-to-bl from-cyan-300/10 via-teal-200/8 to-primary-200/5 rounded-[55%_45%_50%_50%] blur-3xl -z-10" data-id="element-591" />
        {/* Warm orange accent — left side, near section 1 */}
        <div className="absolute top-[28%] -left-[200px] w-[650px] h-[650px] bg-gradient-to-tr from-orange-300/12 via-amber-200/10 to-yellow-200/8 rounded-[40%_60%_55%_45%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '1s'
      }} data-id="element-592" />
        {/* Purple accent — right side, near section 2 */}
        <div className="absolute top-[48%] -right-[150px] w-[550px] h-[550px] bg-gradient-to-bl from-purple-300/10 via-violet-200/8 to-fuchsia-200/6 rounded-[55%_45%_40%_60%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '2.5s'
      }} data-id="element-593" />
        {/* Teal/emerald accent — left side, near section 3 */}
        <div className="absolute top-[72%] -left-[120px] w-[500px] h-[500px] bg-gradient-to-tr from-teal-300/10 via-emerald-200/8 to-cyan-200/6 rounded-[45%_55%_50%_50%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '4s'
      }} data-id="element-594" />
        {/* Small warm glow — near CTA */}
        <div className="absolute bottom-[5%] right-[15%] w-[400px] h-[400px] bg-gradient-to-br from-amber-200/10 via-orange-200/8 to-primary-200/6 rounded-[50%_50%_45%_55%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-595" />

        {/* HERO */}
        <section className="w-full pt-16 pb-12 md:pt-24 md:pb-16 relative overflow-hidden bg-gradient-to-br from-white via-primary-100/40 to-blue-100/40" data-id="element-596">
          <div className="absolute top-0 right-1/4 w-[900px] h-[900px] bg-gradient-to-br from-cyan-600/15 via-teal-400/25 to-blue-400/15 rounded-[60%_40%_55%_45%] blur-3xl -z-10" data-id="element-597" />
          {/* Background illustration — right side */}
          <style data-id="element-598">{`
            @keyframes schools-breathe-img {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.02); }
            }
            @keyframes schools-pulse-glow {
              0%, 100% { opacity: 0; }
              50% { opacity: 1; }
            }
            .schools-illustration {
              transform: translateY(-50%) scale(1);
              transition: transform 1.5s ease-in-out;
            }
            .schools-illustration:hover {
              transform: translateY(-50%) scale(1.04);
            }
            .schools-illustration .schools-img {
              transition: transform 1.5s ease-in-out;
            }
            .schools-illustration:hover .schools-img {
              animation: schools-breathe-img 2.5s ease-in-out infinite;
            }
            .schools-illustration::before {
              content: '';
              position: absolute;
              inset: -40px;
              background: radial-gradient(ellipse at center, rgba(251,146,60,0.4) 0%, rgba(249,115,22,0.3) 30%, rgba(234,88,12,0.2) 50%, transparent 70%);
              opacity: 0;
              transition: opacity 1.5s ease-in-out;
              pointer-events: none;
              z-index: -1;
              filter: blur(30px);
            }
            .schools-illustration:hover::before {
              opacity: 0.6;
              animation: schools-pulse-glow 2.5s ease-in-out infinite;
            }
            .schools-illustration::after {
              content: '';
              position: absolute;
              inset: -20px;
              background: radial-gradient(circle, rgba(6,182,212,0.15) 0%, transparent 60%);
              opacity: 0;
              transition: opacity 1.5s ease-in-out;
              pointer-events: none;
              z-index: -1;
              filter: blur(20px);
            }
            .schools-illustration:hover::after {
              opacity: 0.5;
              animation: schools-pulse-glow 2.5s ease-in-out infinite 0.3s;
            }
          `}</style>
          <div className="schools-illustration absolute right-8 lg:right-16 xl:right-24 2xl:right-32 top-1/2 w-[45%] md:w-[40%] h-full select-none hidden md:block" data-id="element-599">
            <img src="https://cdn.magicpatterns.com/uploads/gY6t59RZsAn2r4jY3LWSnn/Nisa_Duo_5.png" alt="" className="schools-img w-full h-full object-contain object-right opacity-25 lg:opacity-100 pointer-events-none" data-id="element-600" />
          </div>
          <div className="container mx-auto px-6 sm:px-10 md:px-16 lg:px-20" data-id="element-601">
            <div className="max-w-2xl text-left relative" data-id="element-602">
              <div className="absolute -inset-8 bg-gradient-to-r from-white/85 via-white/85 to-transparent blur-xl rounded-3xl -z-[1]" data-id="element-603" />
              <span className="inline-block text-sm uppercase tracking-wider text-primary-500 font-semibold mb-6" data-id="element-604">
                COACHING SYSTEM FOR SCHOOLS & DISTRICTS
              </span>
              <h1 className="font-serif font-medium md:text-display-desktop text-cyan-900 mb-6 md:mb-8 text-[48px]" data-id="element-605">
                Strengthen coaching
                <br data-id="element-606" /> across every school
              </h1>
              <div className="space-y-3 mb-8" data-id="element-607">
                <p className="text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed" data-id="element-608">
                  Nisa gives school leaders visibility into coaching across
                  classrooms and helps coaches deliver more consistent,
                  impactful support.
                </p>
              </div>
              <Button size="lg" onClick={openTalkToUs} data-id="element-609">
                Request a demo
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-610" />
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 1 */}
        <SectionOne data-id="element-611" />

        {/* SECTION 2 */}
        <section className="w-full py-12 md:py-20 bg-primary-50/20 relative overflow-hidden" data-id="element-612">
          {/* Sky abstract blobs */}
          <div className="absolute -top-[80px] -right-[120px] w-[500px] h-[450px] bg-gradient-to-bl from-sky-300/36 via-sky-200/27 to-cyan-100/18 rounded-[45%_55%_60%_40%] blur-3xl pointer-events-none" data-id="element-613" />
          <div className="absolute top-[40%] -left-[150px] w-[400px] h-[380px] bg-gradient-to-tr from-sky-200/32 via-blue-200/22 to-cyan-200/14 rounded-[55%_45%_40%_60%] blur-3xl pointer-events-none" data-id="element-614" />
          <div className="absolute -bottom-[60px] right-[10%] w-[350px] h-[300px] bg-gradient-to-tl from-sky-300/25 via-sky-100/18 to-transparent rounded-[40%_60%_50%_50%] blur-3xl pointer-events-none" data-id="element-615" />
          <div className="container mx-auto px-4 sm:px-6" data-id="element-616">
            <div className="max-w-2xl mx-auto" data-id="element-617">
              <span className="inline-block font-semibold uppercase tracking-widest text-primary-500 mb-3 text-[14px]" data-id="element-618">
                The system
              </span>
              <h2 className="font-serif text-2xl md:text-[1.75rem] font-normal text-cyan-800 mb-5 leading-snug" data-id="element-619">
                A system built for how coaching actually works
              </h2>
              <div className="space-y-8" data-id="element-620">
                <p className="text-[16px] text-gray-600 leading-relaxed" data-id="element-621">
                  Nisa mirrors how coaching actually happens: preparing for
                  visits, reflecting on instruction, and communicating with
                  teachers. Together, these build a complete picture across your
                  schools over time. Coaches don't log anything extra.
                </p>

                {/* Key use cases aside */}
                <div className="pt-6 border-t border-gray-200/60" data-id="element-622">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4 text-center" data-id="element-623">
                    Key Use Cases
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-624">
                    {useCases.map((item, index) => {
                    const Icon = item.icon;
                    return <div key={index} className={`flex items-start gap-4 ${item.bgColor} rounded-xl p-5 border border-gray-100/60 shadow-sm cursor-pointer transition-all duration-300 hover:brightness-[0.92]`} data-id="element-625">
                          <div className="flex items-start gap-4 transition-transform duration-300 group-hover:scale-[1.04] origin-center w-full" style={{
                        transition: 'transform 0.3s ease'
                      }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} data-id="element-626">
                            <div className="flex-shrink-0 flex items-center justify-center mt-0.5" data-id="element-627">
                              <Icon className={`w-6 h-6 ${item.iconColor}`} data-id="element-628" />
                            </div>
                            <div data-id="element-629">
                              <span className="text-base font-medium text-gray-800" data-id="element-630">
                                {item.label}
                              </span>
                              <p className="text-sm text-gray-500 mt-1 leading-snug" data-id="element-631">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 */}
        <section className="w-full py-12 md:py-20 bg-white relative" data-id="element-632">
          <div className="container mx-auto px-4 sm:px-6" data-id="element-633">
            <div className="max-w-2xl mx-auto" data-id="element-634">
              <span className="inline-block font-semibold uppercase tracking-widest text-primary-500 mb-3 text-[14px]" data-id="element-635">
                Your setup
              </span>
              <h2 className="font-serif text-2xl md:text-[1.75rem] font-normal text-cyan-800 mb-5 leading-snug" data-id="element-636">
                Built around your schools
              </h2>
              <div className="space-y-6" data-id="element-637">
                <p className="text-[16px] text-gray-600 leading-relaxed" data-id="element-638">
                  Nisa's system gets customized to reflect your curriculum,
                  coaching frameworks, schedules, and priorities so coaches
                  arrive to a system that already feels familiar.
                </p>
                <ul className="text-[16px] text-gray-600 leading-relaxed space-y-2" data-id="element-639">
                  <li className="flex items-center gap-2" data-id="element-640">
                    <LayoutGridIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-641" />{' '}
                    Your frameworks (e.g. Danielson, RELAY, Get Better Faster)
                  </li>
                  <li className="flex items-center gap-2" data-id="element-642">
                    <GraduationCapIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-643" />{' '}
                    Your curriculum (e.g. Illustrative Math, EL, Great Minds)
                  </li>
                  <li className="flex items-center gap-2" data-id="element-644">
                    <TargetIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-645" />{' '}
                    Your uniquely defined school goals
                  </li>
                </ul>
                <p className="text-gray-900 text-[16px] font-[400]" data-id="element-646">
                  Nisa works the way your schools and coaches work.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="w-full py-12 md:py-16 bg-white relative overflow-hidden" data-id="element-647">
          <div className="container mx-auto px-4 sm:px-6" data-id="element-648">
            <div className="max-w-4xl mx-auto" data-id="element-649">
              <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl shadow-glow p-8 md:p-12 border border-primary-100 text-center" data-id="element-650">
                <h2 className="text-2xl md:text-[1.75rem] font-serif font-normal text-cyan-800 mb-6 leading-snug" data-id="element-651">
                  See how Nisa works for your schools
                </h2>
                <Button size="lg" onClick={openTalkToUs} data-id="element-652">
                  Request a demo
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-653" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-654" />
    </div>;
};